<template>
    <div>
        <procedure-selection v-model="sourceLink.procedure" :isShared="isShared" :isInvalid="sourceLink.errors?.procedureError" :procedureHasValue="procedureHasValue" />
        <div class="flex justify-content-start flex-wrap md:flex-nowrap gap-2 overflow-x-auto">
            <!-- 
                Operators for Procedure
                Normally Operators are to be shown in most cases here 
                but there is a special case incase of attribute so we should avoid ProcedureCase4
            -->
            <procedure-operator v-if="filteredOperators && filteredOperators.length > 0 && !ProcedureCase4" v-model="sourceLink.operator" :operators="filteredOperators" :isInvalid="sourceLink.errors?.operatorError" />
            <!-- Procedure Selection ==>  Procedure Operator ==>  Value format works on every cases except for attribute 
                Values are based on cases, for more details check below in the computed properties for each cases
                since attribute option have its own pattern-->
            <div class="flex flex-column gap-2 w-full md:w-14rem">
                <!-- 
                    Label Value will be visible in all cases except for ProcedureCase4
                -->

                <label v-if="!ProcedureCase4" for="value" class="font-bold">Value: *</label>
                <!-- CASE 1: user_today_age / assessment_user_age -->
                <InputNumber v-if="ProcedureCase1" id="value" v-model="sourceLink.value" placeholder="Enter the age in yrs" :min="1" :max="100" :minFractionDigits="0" suffix=" yrs" :class="{ 'p-invalid': isInvalid }" />
                <!-- CASE 2: assessment_date -->
                <calendar-input v-else-if="ProcedureCase2" v-model="sourceLink.value" :isInvalid="isInvalid" />
                <!-- CASE 3: assessment_code -->
                <assessment-dropdown v-else-if="ProcedureCase3" v-model="sourceLink.value" :isInvalid="isInvalid" :userProfile="true" />
                <!-- CASE 4: attribute, first_attribute, last_attribute -->
                <!-- v-model="sourceLink.value" -->
                <!-- :assessmentCode="sourceLink.assessmentCode" -->
                <attribute-dropdown v-else-if="ProcedureCase4" :sourceLink="sourceLink">
                    <!-- Assessment Filter -->
                    <template #assessment-selection>
                        <div class="flex flex-column gap-2">
                            <label for="assessmentValue" class="font-bold">Assessment:</label>
                            <assessment-dropdown v-model="sourceLink.assessment_code" :userProfile="true" />
                        </div>
                    </template>
                    <!-- Procedure Operator -->
                    <template #operator>
                        <procedure-operator v-model="sourceLink.operator" :operators="filteredOperators" :isInvalid="sourceLink.errors?.operatorError" />
                    </template>
                </attribute-dropdown>
                <!-- CASE 5: pass_fail, user_profile, active_only_assessment, active_only_attribute -->
                <binary-checkbox v-else-if="ProcedureCase5" v-model="sourceLink.value" :isInvalid="isInvalid" />
                <!-- CAS 6: user_list -->
                <user-list-multi-select v-else-if="ProcedureCase6" v-model="sourceLink.value" :isInvalid="isInvalid" />
                <!-- Error validation for complusory selection but doesnt apply to Procedure4 case since it handles on its own -->
                <small v-if="isInvalid && !ProcedureCase4">Value field is mandatory</small>
            </div>
        </div>
    </div>
</template>

<script setup>
import ProcedureSelection from './ProcedureSelection.vue';
import ProcedureOperator from './ProcedureOperator.vue';
// procedures types based values components
import CalendarInput from '../../../../../../components/user-groups/components/CalendarInput.vue';
import AssessmentDropdown from '../../../../components/AssessmentDropdown.vue';
import BinaryCheckbox from '../../../../components/BinaryCheckbox.vue';
import UserListMultiSelect from '../../../../components/UserListMultiSelect.vue';
import AttributeDropdown from '../../../../components/AttributeDropdown.vue';

import { ref, defineProps, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useDataSourceStore } from '../../store';
import { Operator, Procedure } from '../../enums';
import { getAllOperators } from '../../enum-helpers';

// props
const props = defineProps({
    link: {
        type: Object,
    },
});

// store states
const { editDataSource } = storeToRefs(useDataSourceStore());

// states
const operators = ref(getAllOperators());

// computed properties
const sourceLink = computed(() => {
    return props.link;
});

// Returns if edit data source is shared
const isShared = computed(() => {
    return editDataSource.value.isShared;
});

const procedureHasValue = computed(() => {
    return sourceLink.value ? true : false;
});

// Checks if any source link is invalid and have errors
const isInvalid = computed(() => {
    return (sourceLink.value.errors && sourceLink.value.errors.valueError) ?? false;
});

// It will return the list of operators that is supported for the current selected procedure
const filteredOperators = computed(() => {
    let returnVal = null;
    switch (sourceLink.value.procedure) {
        case Procedure.USER_TODAY_AGE:
        case Procedure.ASSESSMENT_DATE:
        case Procedure.ASSESSMENT_USER_AGE:
        case Procedure.ATTRIBUTE:
        case Procedure.FIRST_ATTRIBUTE:
        case Procedure.LAST_ATTRIBUTE:
            returnVal = operators.value;
            break;

        case 'assessment_code':
            returnVal = operators.value?.filter((operator) => [Operator.EQ, Operator.NTE].includes(operator.value));
            break;

        default: // default case for pass_fail, user_profile and user_list where operator is not even required
            break;
    }
    return returnVal;
});

//watchers

watch(
    () => sourceLink.value.procedure,
    () => {
        sourceLink.value.value = null;
        // sourceLink.value.operator = null;
    }
);

// Procedure Cases starts
// Case 1: user_today_age OR assessment_user_age
const ProcedureCase1 = computed(() => {
    return sourceLink.value.procedure === Procedure.USER_TODAY_AGE || sourceLink.value.procedure === Procedure.ASSESSMENT_USER_AGE;
});

// Case 2: assessment_date
const ProcedureCase2 = computed(() => {
    return sourceLink.value.procedure === Procedure.ASSESSMENT_DATE;
});

// Case 3: assessment_code
const ProcedureCase3 = computed(() => {
    return sourceLink.value.procedure === Procedure.ASSESSMENT_CODE ||  sourceLink.value.procedure === Procedure.WITHOUT_ASSESSMENT_CODE;
});

// Case 4: attribute OR first_attribute OR last_attribute -> for attributes scenario
const ProcedureCase4 = computed(() => {
    return sourceLink.value.procedure === Procedure.ATTRIBUTE || sourceLink.value.procedure === Procedure.FIRST_ATTRIBUTE || sourceLink.value.procedure === Procedure.LAST_ATTRIBUTE;
});

// Case 5: pass_fail OR user_profile OR active_only_assessment OR active_only_attribute
const ProcedureCase5 = computed(() => {
    switch (sourceLink.value.procedure) {
        case Procedure.PASS_FAIL:
        case Procedure.USER_PROFILE:
        case Procedure.ACTIVE_ONLY_ASSESSMENT:
        case Procedure.ACTIVE_ONLY_ATTRIBUTE:
            return true;
        default:
            return false;
    }
});

const ProcedureCase6 = computed(() => {
    return sourceLink.value.procedure === Procedure.USER_LIST;
});
</script>
